import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import classNames from 'classnames'
import { format, formatDistanceToNow, parseISO } from 'date-fns'
import { IconChevronDown, IconChevronUp, IconLink } from '@tabler/icons-react'
import { Button } from '@/components/buttons/Button'
import { ActionsDropdown } from '@/components/dropdowns/ActionsDropdown'
import { DoubleChevron } from '@/components/icons/DoubleChevron'
import { Pill } from '@/components/pills/Pill'
import { CopyButton } from '@/components/buttons/CopyButton'
import { toast } from '@/components/toasts/toast.helpers'
import { CHANNEL_TYPE, dateFormat } from '@/utils/constants'
import { parseCallExplorerParams } from '../Calls/components/helpers'
import {
  cleanMessageHtml,
  getAnchorId,
  getDateTimeISO,
  highlightSearch,
  isSmsOrChatMessage,
  splitQuotedContent,
  getCollapsedMessageHtml,
  isMessageExpandable,
  parseExternalId,
} from './helpers'
import './MessagesPage.scss'

export const MessageTranscript = ({
  message,
  searchQuery,
  keywords,
  currentSearchIndex,
  matchIndices,
  setSearchMatchesCount,
  selectMessageForTask,
  expanded,
  selectedKeyword,
}) => {
  const location = useLocation()
  const { highlightedMessage } = parseCallExplorerParams(location)

  const [expandedMetadata, setExpandedMetadata] = useState(false)
  const [expandedQuotes, setExpandedQuotes] = useState({})
  const [isTemporarilyHighlighted, setIsTemporarilyHighlighted] = useState(false)
  const isSmsOrChat = isSmsOrChatMessage(message.message_type)
  const [expandedContent, setExpandedContent] = useState(isSmsOrChat ? true : expanded)

  useEffect(() => {
    if (!isSmsOrChat) {
      setExpandedContent(expanded)
    }
  }, [expanded, isSmsOrChat])

  const toggleCollapsedContent = () => {
    setExpandedContent((prev) => !prev)
  }

  const isAgent = message.is_sender_agent
  const sentAtISO = getDateTimeISO(message.sent_at)
  const dateShort = formatDistanceToNow(parseISO(sentAtISO), { addSuffix: true })
  const dateLong = format(new Date(sentAtISO), dateFormat.long)
  const timeOnly = format(new Date(sentAtISO), 'h:mm a')
  const anchorId = getAnchorId(sentAtISO)

  useEffect(() => {
    let timeout
    if (highlightedMessage === anchorId) {
      setIsTemporarilyHighlighted(true)
      const element = document.getElementById(anchorId)
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
      timeout = setTimeout(() => {
        setIsTemporarilyHighlighted(false)
      }, 2000)
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [highlightedMessage, anchorId])

  const handleAgentClick = () => {
    if (isSmsOrChat && isAgent) {
      toggleCollapsedContent()
    }
  }

  const handleCopyLink = (event) => {
    event.stopPropagation()
    const url = new URL(window.location.href)
    url.searchParams.set('highlightedMessage', anchorId)
    navigator.clipboard
      .writeText(url.toString())
      .then(() => toast.success('Link copied to clipboard!'))
      .catch(() => toast.error('Failed to copy link'))
  }

  const [messageIdCopied, setMessageIdCopied] = useState(false)
  const messageIdCopiedTimeoutRef = useRef(null)
  useEffect(
    () => () => {
      if (messageIdCopiedTimeoutRef.current) {
        clearTimeout(messageIdCopiedTimeoutRef.current)
      }
    },
    []
  )
  const displayMessageId = parseExternalId(message.eml_message_id) || message.id
  const handleCopyMessageId = (event) => {
    event.stopPropagation()
    navigator.clipboard
      .writeText(String(displayMessageId))
      .then(() => {
        setMessageIdCopied(true)
        if (messageIdCopiedTimeoutRef.current) {
          clearTimeout(messageIdCopiedTimeoutRef.current)
        }
        messageIdCopiedTimeoutRef.current = setTimeout(() => {
          setMessageIdCopied(false)
          messageIdCopiedTimeoutRef.current = null
        }, 1000)
      })
      .catch(() => toast.error('Failed to copy message ID'))
  }
  const handleMessageIdKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleCopyMessageId(event)
    }
  }

  const { processedContent, matchCount } = useMemo(() => {
    const cleanedDoc = cleanMessageHtml(message?.html_body || message?.transcript)
    const { mainContent, quotedSections } = splitQuotedContent(cleanedDoc)

    // Apply search highlighting to both main content and quoted sections
    const mainDoc = new DOMParser().parseFromString(mainContent, 'text/html')
    const highlightedMain = highlightSearch(mainDoc, searchQuery, matchIndices, currentSearchIndex)

    let processedMatchCount = highlightedMain.matchCount
    const highlightedQuotes = quotedSections.map((section, sectionIndex) => {
      const quoteDoc = new DOMParser().parseFromString(section.html, 'text/html')
      const sectionIndices = matchIndices.slice(processedMatchCount)
      const highlighted = highlightSearch(quoteDoc, searchQuery, sectionIndices, currentSearchIndex)

      const containsCurrentMatch = sectionIndices.includes(currentSearchIndex)
      processedMatchCount += highlighted.matchCount
      return {
        ...section,
        highlightedHtml: highlighted.html,
        matchCount: highlighted.matchCount,
        containsCurrentMatch,
        sectionIndex,
      }
    })

    return {
      processedContent: {
        mainContent: highlightedMain.html,
        quotedSections: highlightedQuotes,
      },
      matchCount:
        highlightedMain.matchCount +
        highlightedQuotes.reduce((sum, q) => sum + (q.matchCount || 0), 0),
    }
  }, [
    message?.html_body,
    message?.transcript,
    searchQuery,
    matchIndices,
    currentSearchIndex,
    keywords,
  ])

  const plainTextContent = useMemo(() => {
    const html = message?.html_body || message?.transcript
    if (!html) return ''
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html
    return tempDiv.textContent || tempDiv.innerText || ''
  }, [message?.html_body, message?.transcript])

  // Report to parent
  useEffect(() => {
    setSearchMatchesCount(matchCount)
  }, [matchCount])

  useEffect(() => {
    const el = document.querySelector('.current-match')
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [processedContent])

  const isKeywordInQuotedSection = (sectionHtml, keyword) => {
    if (!keyword) return false
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = sectionHtml
    const text = tempDiv.textContent.toLowerCase()
    return text.includes(keyword.toLowerCase())
  }

  useEffect(() => {
    if (selectedKeyword && processedContent.quotedSections) {
      const newExpandedQuotes = {}
      processedContent.quotedSections.forEach((section, index) => {
        if (isKeywordInQuotedSection(section.highlightedHtml, selectedKeyword)) {
          newExpandedQuotes[index] = true
        }
      })
      setExpandedQuotes(newExpandedQuotes)
    } else if (searchQuery && expanded && processedContent.quotedSections) {
      const newExpandedQuotes = {}
      processedContent.quotedSections.forEach((section) => {
        if (section.matchCount > 0) {
          newExpandedQuotes[section.sectionIndex] = true
        }
      })
      setExpandedQuotes(newExpandedQuotes)
    } else {
      setExpandedQuotes({})
    }
  }, [selectedKeyword, processedContent])

  const toggleQuotedSection = (index) => {
    setExpandedQuotes((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  const taskAction = (type, label) => ({
    label,
    testId: `create-${type}-task-action`,
    onClick: () =>
      selectMessageForTask({
        anchorId,
        callId: message.call_id,
        channel: message.message_type,
        messageId: message.id,
        type,
        userId: message.user_id,
      }),
  })

  const messageActions = [
    taskAction('coaching', 'Create Coaching Task'),
    taskAction('compliance', 'Create Compliance Task'),
  ]

  const renderQuotedSection = (section, index) => {
    const sectionKey = `${message.id || 'message'}-quote-${index}`
    const isExpanded = expandedQuotes[index]
    const sectionType = section.type === 'replied' ? 'quoted' : 'forwarded'

    return (
      <div key={sectionKey} className="mt">
        <Button
          small
          className="no-wrap"
          onClick={() => toggleQuotedSection(index)}
          data-testid={`show-quoted-${index}`}
        >
          {isExpanded ? (
            <>
              <IconChevronUp />
              Hide {sectionType} text
            </>
          ) : (
            <>
              <IconChevronDown />
              Show {sectionType} text
            </>
          )}
        </Button>

        {isExpanded && (
          <div
            className="force-wrap"
            /* eslint-disable-next-line react/no-danger */
            dangerouslySetInnerHTML={{ __html: section.highlightedHtml }}
          />
        )}
      </div>
    )
  }

  const hasQuotedSections = processedContent.quotedSections.length > 0
  const isExpandable = isMessageExpandable(message?.html_body || message?.transcript)
  const shouldBeMuted = !expandedContent && isExpandable

  return (
    <div
      className={classNames('left-indicator', {
        'left-indicator--agent': !isSmsOrChat && isAgent,
        'left-indicator--sms-chat': isSmsOrChat && !isAgent,
        'left-indicator--sms-chat-agent': isSmsOrChat && isAgent,
        'highlighted-message': !isSmsOrChat && highlightedMessage === anchorId,
      })}
      data-testid="message"
      id={anchorId}
    >
      {isSmsOrChat ? (
        <div className="sms-chat-message">
          <div className="sms-chat-header-container">
            <Pill circular className="uppercase" brand={isAgent} emphasized={!isAgent}>
              {isAgent ? (message.username.length > 0 ? message.username[0] : '-') : 'C'}
            </Pill>
            <div
              className={classNames('sms-chat-header-row flex-column', {
                'agent-header': isAgent,
                'customer-header': !isAgent,
                'sms-chat-highlight': isTemporarilyHighlighted,
              })}
            >
              <div className="flex-align-center full-width">
                <span className={classNames({ clickable: isAgent })} onClick={handleAgentClick}>
                  {isAgent ? message.username : 'Customer'}
                </span>
                <div className="flex-item" />
                <div className="sms-chat-actions">
                  <Pill
                    small
                    bold
                    clickable
                    className="message-id-pill"
                    tooltip="Copy message ID"
                    onClick={handleCopyMessageId}
                    onKeyDown={handleMessageIdKeyDown}
                    role="button"
                    tabIndex={0}
                    dataTestId="message-id-pill"
                  >
                    {messageIdCopied ? 'Copied!' : displayMessageId}
                  </Pill>
                  <Button small square onClick={handleCopyLink} tooltip="Copy link to this message">
                    <IconLink size={16} />
                  </Button>
                  <CopyButton
                    content={plainTextContent}
                    name="message"
                    iconOnly
                    showTooltip
                    tooltipContent="Copy message text"
                  />
                </div>
              </div>
              <div
                className={expandedContent ? 'force-wrap' : 'truncate-message'}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: expandedContent
                    ? processedContent.mainContent
                    : getCollapsedMessageHtml(processedContent.mainContent, hasQuotedSections),
                }}
              />
              {/* Time row */}
              <div className="flex-align-center">
                <div className="flex-item" />
                <div className="sms-chat-time">{timeOnly}</div>
              </div>
            </div>
          </div>

          {expandedContent && processedContent.quotedSections.map(renderQuotedSection)}
        </div>
      ) : (
        <div
          className="flex-align-center full-width gap mb transcript-header clickable"
          onClick={toggleCollapsedContent}
        >
          <Pill circular className="uppercase" brand={isAgent} emphasized={!isAgent}>
            {isAgent ? (message.username.length > 0 ? message.username[0] : '-') : 'C'}
          </Pill>
          <span>{isAgent ? message.username : 'Customer'}</span>
          <div className="flex-item">
            <Button
              basic
              square
              small
              onClick={(e) => {
                e.stopPropagation()
                setExpandedMetadata(!expandedMetadata)
              }}
              data-testid="expand-collapse-button"
            >
              <DoubleChevron flipped={expandedMetadata} />
            </Button>
          </div>
          <span>{dateShort}</span>
          <ActionsDropdown buttonProps={{ basic: true }} actions={messageActions} />
        </div>
      )}
      {!isSmsOrChat && expandedMetadata && (
        <div className="message-info mb">
          {message.message_type === CHANNEL_TYPE.EMAIL && (
            <>
              <div>{`from: ${message.from_ref}`}</div>
              <div>{`to: ${message.to_ref}`}</div>
            </>
          )}
          <div>{`date: ${dateLong}`}</div>
        </div>
      )}

      {/* Main message content - only for EMAIL */}
      {!isSmsOrChat && (
        <div
          data-testid="message-content"
          className={classNames('message-content', {
            'muted-text': shouldBeMuted,
            clickable: !expandedContent,
          })}
          onClick={() => {
            if (!expandedContent) toggleCollapsedContent()
          }}
        >
          <div
            className={expandedContent ? 'force-wrap' : 'truncate-message'}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: expandedContent
                ? processedContent.mainContent
                : getCollapsedMessageHtml(processedContent.mainContent, hasQuotedSections),
            }}
          />
        </div>
      )}

      {!isSmsOrChat && expandedContent && processedContent.quotedSections.map(renderQuotedSection)}
    </div>
  )
}
